import React, { Component } from 'react'
import { Query } from '@apollo/client/react/components'
import { withApollo } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import { Alert, Select } from 'antd'
import { injectIntl } from 'react-intl'
import { compact, sortBy } from 'lodash'

import * as Auth from '../auth'
import { displayError, displayGraphQLErrors } from './Utils'

const Option = Select.Option

export const MontyUsersQuery = gql`
	query montyUsers {
		users(filter: { montyUser: true }) {
			_id
			firstName
			lastName
			username
		}
	}
`

// the owner mutation depends on the type of program (music or message)
const OwnerMutations = {
	program: gql`
		mutation setProgramOwner($id: String!, $ownerId: String) {
			programUpdateById(_id: $id, record: { ownerId: $ownerId }) {
				recordId
			}
		}
	`,
	messageProgram: gql`
		mutation setMessageProgramOwner($id: String!, $ownerId: String) {
			messageProgramUpdateById(_id: $id, record: { ownerId: $ownerId }) {
				recordId
			}
		}
	`
}

const userLabel = (user) =>
	compact([user.firstName, user.lastName]).join(' ') || user.username

/**
 * Dropdown to select the spectre internal owner of a music or message program.
 * The list is populated with the spectre internal users (montyUser = true)
 * and the program is updated as soon as the owner is changed.
 *
 * Props:
 * - programType: 'program' | 'messageProgram'
 * - programId: the program _id
 * - teamId: the program teamId (for access check)
 * - ownerId: the current owner user _id
 * - refetchQueries: passed to the update mutation
 */
class SelectProgramOwner extends Component {
	state = { saving: false }

	setOwner = async (ownerId) => {
		const { client, programType, programId, refetchQueries } = this.props
		this.setState({ saving: true })
		try {
			await client.mutate({
				variables: { id: programId, ownerId: ownerId ?? null },
				mutation: OwnerMutations[programType],
				refetchQueries
			})
		} catch (e) {
			displayError(e)
		}
		this.setState({ saving: false })
	}

	render() {
		const { intl, programType, teamId, ownerId } = this.props

		const canEdit =
			Auth.hasPermission(`${programType}:edit:setOwner`) &&
			Auth.hasAccess(`${programType}:edit`, teamId)

		return (
			<Query query={MontyUsersQuery}>
				{({ loading, error, data }) => {
					if (error) {
						return (
							<Alert
								message={intl.formatMessage({ id: 'error' })}
								description={displayGraphQLErrors(error)}
								type="error"
								showIcon
							/>
						)
					}

					const users = sortBy(data?.users || [], userLabel)

					return (
						<Select
							showSearch
							allowClear
							style={{ minWidth: 250 }}
							loading={loading || this.state.saving}
							disabled={!canEdit || this.state.saving}
							placeholder={intl.formatMessage({
								id: 'programOwner.placeholder'
							})}
							value={
								// keep the placeholder displayed while the
								// users are loading or when there is no owner
								loading ? undefined : (ownerId ?? undefined)
							}
							optionFilterProp="children"
							onChange={(value) => this.setOwner(value ?? null)}
						>
							{users.map((user) => (
								<Option key={user._id} value={user._id}>
									{userLabel(user)}
								</Option>
							))}
						</Select>
					)
				}}
			</Query>
		)
	}
}

export default withApollo(injectIntl(SelectProgramOwner))
